import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { navigate } from '../../app/hashRoute';
import { ApiClientError, apiClient, formatCents } from '../../core/api';
import type { CreatedPaymentOrder, PaymentType } from '../../core/api';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';
import { billingMeta, contentStyle } from './componentMeta';
import { paymentCheckoutUrl, redirectToPaymentCheckout } from './checkout';
import styles from '../account/commerce.module.css';

function Registered({ meta, children, className, surface }: { meta: ComponentMetaInput; children?: ReactNode; className?: string; surface?: 'page' | 'panel' | 'control' | 'overlay' }): ReactElement {
  const complete = buildComponentMeta(meta, meta.role ?? 'container');
  const ref = useComponentMeta<HTMLDivElement>(complete);
  return <div ref={ref} className={className} data-ui-id={complete.id} data-ui-role={complete.role} data-skin-surface={surface} style={contentStyle(meta.layout)}>{children}</div>;
}

function PaymentButton({ paymentType, disabled }: { paymentType: PaymentType; disabled: boolean }): ReactElement {
  const meta = buildComponentMeta(billingMeta(`billing.pay.${paymentType}`));
  const ref = useComponentMeta<HTMLButtonElement>(meta);
  return <button ref={ref} data-ui-id={meta.id} data-ui-role={meta.role} className={styles.primary} type="submit" name="paymentType" value={paymentType} disabled={disabled}>{meta.label}</button>;
}

export function BillingPage(): ReactElement {
  const [amount, setAmount] = useState('10');
  const [orders, setOrders] = useState<(CreatedPaymentOrder & { amount: number })[]>([]);
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const mounted = useRef(false);
  const running = useRef(false);
  const formRef = useComponentMeta<HTMLFormElement>(buildComponentMeta(billingMeta('billing.form')));
  const ordersRef = useComponentMeta<HTMLElement>(buildComponentMeta(billingMeta('billing.orders')));

  useEffect(() => {
    mounted.current = true;
    let active = true;
    void apiClient.currentUser().then(() => { if (active) setLoggedIn(true); }).catch((requestError: unknown) => {
      if (!active) return;
      if (requestError instanceof ApiClientError && requestError.status === 401) setLoggedIn(false);
      else setError(requestError instanceof Error ? requestError.message : '账户状态加载失败');
    });
    return () => { active = false; mounted.current = false; };
  }, []);

  async function createOrder(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (running.current || loggedIn !== true) return;
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const paymentType = submitter instanceof HTMLButtonElement ? submitter.value : undefined;
    if (paymentType !== 'wxpay' && paymentType !== 'alipay') {
      setError('请选择微信支付或支付宝支付');
      return;
    }
    running.current = true;
    setBusy(true); setError(null); setMessage(null);
    try {
      const numericAmount = Number(amount);
      const order = await apiClient.createPaymentOrder(numericAmount, paymentType);
      if (!mounted.current) return;
      setOrders((current) => [{ ...order, amount: numericAmount }, ...current]);
      const checkoutUrl = paymentCheckoutUrl(order.paymentUrl);
      if (checkoutUrl) {
        setMessage('订单已创建，请在支付页面完成付款。');
        redirectToPaymentCheckout(checkoutUrl);
      } else {
        setError('订单已创建，但支付链接不可用，请联系管理员。');
      }
    } catch (requestError) { if (mounted.current) setError(requestError instanceof Error ? requestError.message : '创建充值订单失败'); }
    finally { running.current = false; if (mounted.current) setBusy(false); }
  }

  async function refreshOrder(orderId: string): Promise<void> {
    if (running.current) return;
    running.current = true;
    setBusy(true); setError(null);
    try {
      const latest = await apiClient.paymentOrder(orderId);
      if (!mounted.current) return;
      setOrders((current) => current.map((order) => order.orderId === orderId ? { ...order, status: latest.status, amount: latest.amountCents / 100 } : order));
    } catch (requestError) { if (mounted.current) setError(requestError instanceof Error ? requestError.message : '订单状态加载失败'); }
    finally { running.current = false; if (mounted.current) setBusy(false); }
  }

  if (loggedIn === false) return <Registered meta={billingMeta('billing.root')} className={styles.page} surface="page"><Registered meta={billingMeta('billing.header')} className={styles.header} /><Registered meta={billingMeta('billing.title')} className={styles.title}>充值中心</Registered><div className={styles.scroll}><div className={styles.notice}><p>充值需要先登录账户。</p><button className={styles.primary} type="button" onClick={() => navigate('/account')}>前往登录</button></div></div></Registered>;

  return <Registered meta={billingMeta('billing.root')} className={styles.page} surface="page">
    <Registered meta={billingMeta('billing.header')} className={styles.header} />
    <Registered meta={billingMeta('billing.title')} className={styles.title}>充值中心</Registered>
    <div className={styles.scroll}>
      <form ref={formRef} data-ui-id="billing.form" data-ui-role="container" className={styles.card} onSubmit={(event) => void createOrder(event)}>
        <h2 className={styles.sectionTitle}>充值栀籽</h2>
        <div className={styles.amountGrid}>{['10', '50', '100'].map((preset) => <button className={amount === preset ? `${styles.amountButton} ${styles.amountButtonActive}` : styles.amountButton} key={preset} type="button" disabled={busy} onClick={() => setAmount(preset)}>{preset} 元</button>)}</div>
        <label className={styles.label} htmlFor="billing-amount">自定义金额</label>
        <input id="billing-amount" className={styles.input} type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(event.currentTarget.value)} disabled={busy} required />
        <div className={styles.row} aria-busy={busy}>
          <PaymentButton paymentType="wxpay" disabled={busy || loggedIn !== true} />
          <PaymentButton paymentType="alipay" disabled={busy || loggedIn !== true} />
        </div>
        <p className={styles.hint}>支付成功以服务端异步回调为准，浏览器返回页不会直接增加余额。</p>
      </form>
      {message ? <p className={styles.success} role="status">{message}</p> : null}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <section ref={ordersRef} data-ui-id="billing.orders" data-ui-role="list" className={styles.card}><h2 className={styles.sectionTitle}>本次会话订单</h2>{orders.length === 0 ? <p className={styles.empty}>暂无订单</p> : orders.map((order) => <div className={styles.ledgerItem} key={order.orderId}><div className={styles.row}><span className={styles.orderTitle}>#{order.orderId.slice(0, 8)}</span><strong>{formatCents(Math.round(order.amount * 100))} 元</strong></div><p className={styles.muted}>状态：{order.status}{order.status === 'pending' && paymentCheckoutUrl(order.paymentUrl) ? <a className={styles.link} href={paymentCheckoutUrl(order.paymentUrl)} target="_blank" rel="noopener noreferrer"> 继续支付</a> : null}</p><button className={styles.secondary} type="button" disabled={busy} onClick={() => void refreshOrder(order.orderId)}>刷新订单状态</button></div>)}</section>
    </div>
  </Registered>;
}
